import { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useCompanyInfoQuery, useUploadCompanyLogoMutation } from '../../../../../graphql/generated/graphql';

import { useLoginState } from '../../../../hooks/loginInfo';
import { useUserInfoState } from '../../../../hooks/userInfo';
import { setCompanyInfo, useCompanyInfoState } from '../../../../hooks/companyInfo';

import AddLogoCompany from '../../../components/company/add-logo';
import { CompanyIInfo, ItemCompanySettings } from '../../../../config/interfaces';
import ConfigProjectLayout from '../../../../theme/templates/project/config';
import AddCompanyManager from '../../../components/company/add-company-managers';
import { getBase64 } from '../../../../helper/images';
import { LogoCompanySVG } from '../../../../assets/svg';

export default function SettingsCompanyPage() {
  const loginState = useLoginState();
  const userInfoState = useUserInfoState();
  const [logoMutation] = useUploadCompanyLogoMutation();
  const companyState = useCompanyInfoState();
  const [loader, setLoader] = useState(false);
  const companyInfo = companyState.get();
  const [items, setItems] = useState<ItemCompanySettings[]>([]);
  const { data: companyInfoData } = useCompanyInfoQuery({
    variables: {
      company_id: loginState.company_id.get() as string,
    },
  });

  const image = companyInfo.url_file
    ? <img className="image-logo" src={companyInfo.url_file} alt="logo" />
    : <LogoCompanySVG />;

  const onLoad = async (file: File) => {
    setLoader(true);
    getBase64(file)
      .then(async (logo) => {
        try {
          const response = await logoMutation({
            variables: {
              logo,
            },
          });
          if (response.data?.uploadCompanyLogo.url_file) {
            companyState.url_file.set(response.data?.uploadCompanyLogo.url_file);
            userInfoState.company.set({
              ...userInfoState.company.get(),
              url_file: response.data?.uploadCompanyLogo.url_file,
            })
          }
          setLoader(false);
        } catch (e: any) {
          console.error(e);
        }
      });
  };

  useEffect(() => {
    if (companyInfoData?.companyInfo) {
      setCompanyInfo(companyInfoData.companyInfo as CompanyIInfo);
      setItems([
        {
          id: 1,
          title: 'Company Name',
          label: companyInfo.company_name,
        },
        {
          id: 2,
          title: 'Email',
          label: companyInfo.email,
        },
        {
          id: 3,
          title: 'Company Address',
          label: companyInfo.address,
        },
        {
          id: 4,
          title: 'Phone',
          label: companyInfo.phone,
        },
      ]);
    }
  }, [companyInfoData]);

  return (
    <>
      {
        companyInfo && (
          <ConfigProjectLayout>
            <Typography
              component="h1"
              variant="h3"
              sx={{ fontWeight: 500 }}
            >
              Company settings
            </Typography>
            <Grid
              container
              sx={{
                mt: 4,
                width: '100%',
              }}
            >
              <Grid
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <Grid
                  item
                  sx={{
                    width: '194px',
                    maxWidth: '90%',
                    height: '186px',
                  }}
                >
                  {loader ? <CircularProgress size={100} color="primary" /> : <AddLogoCompany image={image} onLoad={onLoad} />}
                </Grid>
                {
                  items.length > 0 && (
                    <Grid
                      sx={{
                        width: 'calc(100% - 250px)',
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        mt: 2
                      }}
                    >
                      {items.map((item) => (
                        <Grid
                          key={item.id}
                          item
                          sx={{
                            width: '50%',
                            maxWidth: '100%',
                            minWidth: '300px',
                            mb: 4,
                          }}
                        >
                          <Typography
                            component="p"
                            variant="h5"
                            sx={{ fontWeight: 500 }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            component="p"
                            variant="h4"
                            sx={{
                              mt: 2,
                              fontWeight: 300,
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  )
                }
              </Grid>
            </Grid>
            <Grid
              sx={{
                width: '100%',
                mt: 4,
              }}
            >
              <Typography
                component="h4"
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Company Managers
              </Typography>
              <AddCompanyManager />
            </Grid>
          </ConfigProjectLayout>
        )
      }
    </>
  );
}
